import React, { useCallback, useEffect, useState } from "react";
import * as Updates from "expo-updates";
import * as Sentry from "@sentry/react-native";
import { RefreshControl, ScrollView } from "react-native";

import AppUpdateSection from "./Components/AppUpdateSection";

import ScreenLayout from "@/Components/Layouts/Native/ScreenLayout";
import {
  AppUpdateType,
  InitialUnknownUpdate,
  InitialUnknownVersion,
  type UpdateComparison,
} from "@/schemas/app/update";
import { AppVariant } from "@/utils/expo";
import SystemService from "@/services/internal/system";
import { getAppVersionString, getLatestAppVersion } from "@/utils/version";

const AppUpdatesScreen = (): React.JSX.Element => {
  const { currentlyRunning } = Updates.useUpdates();
  const [refreshing, setRefreshing] = useState(false);
  const [latestAppUpdate, setLatestAppUpdate] =
    useState<UpdateComparison>(InitialUnknownUpdate);
  const [latestAppVersion, setLatestAppVersion] = useState<UpdateComparison>(
    InitialUnknownVersion,
  );

  const fetchLatestUpdates = async (): Promise<void> => {
    try {
      const update = await Updates.checkForUpdateAsync();
      const currentUpdateId =
        currentlyRunning.updateId?.substring(0, 10) || AppVariant.UNKNOWN;

      const latestUpdateId = update.isAvailable
        ? update.manifest?.id.substring(0, 10) || AppVariant.UNKNOWN
        : currentUpdateId;

      const expo_update_version: UpdateComparison = {
        currentVersion: currentUpdateId,
        latestVersion: latestUpdateId,
        isUpdateAvailable: update.isAvailable,
        type: AppUpdateType.EXPO,
      };
      setLatestAppUpdate(expo_update_version);
    } catch (error) {
      Sentry.captureException(error);
    }
  };

  const fetchLatestVersions = async (): Promise<void> => {
    const systemService = new SystemService();
    try {
      const results = await systemService.getSupportedVersion();
      const latestVersion = getLatestAppVersion(results);
      const latestAppVersionString = getAppVersionString(latestVersion);
      const app_version: UpdateComparison = {
        currentVersion: currentlyRunning.runtimeVersion || "Unknown",
        latestVersion: latestAppVersionString,
        isUpdateAvailable:
          true || currentlyRunning.runtimeVersion !== latestAppVersionString,
        type: AppUpdateType.APP,
      };
      setLatestAppVersion(app_version);
    } catch (error) {
      Sentry.captureException(error);
    }
  };

  const fetchAndSetUpdatesAndVersions = async (): Promise<void> => {
    setRefreshing(true);
    await fetchLatestUpdates();
    await fetchLatestVersions();
    setRefreshing(false);
  };

  useEffect(() => {
    fetchAndSetUpdatesAndVersions();
  }, []);

  const onRefresh = useCallback(() => {
    fetchAndSetUpdatesAndVersions();
  }, []);

  return (
    <ScreenLayout isLoading={false}>
      <ScrollView
        refreshControl={
          <RefreshControl refreshing={refreshing} onRefresh={onRefresh} />
        }
      >
        <AppUpdateSection
          updateType={AppUpdateType.EXPO}
          comparison={latestAppUpdate}
        />
        <AppUpdateSection
          updateType={AppUpdateType.APP}
          comparison={latestAppVersion}
        />
      </ScrollView>
    </ScreenLayout>
  );
};

export default AppUpdatesScreen;
